// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import {
  alpha,
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  createFilterOptions,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { KeyboardEvent, SyntheticEvent, UIEvent, useCallback, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import { CommonIconPlus } from '~/assets/svgComponents';
// Others
import { varColor } from '~/themes/themeVariables';
import { IBaseOption, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import { DEFAULT_CURRENT_PAGE, EMPTY_STRING, ENTER_KEY, LOADING_OPTION_VALUE, SPACE } from '~/utils/constants/common';
// Styles, images, icons
import styles from './BaseAutoCompleteComponent.module.scss';
import { icons } from '~/assets';

type Props<T> = {
  id?: string;
  label?: string;
  placeholder?: string;
  width?: number | string;
  height?: number | string;
  value?: string | string[] | null;
  multiple?: boolean;
  maxItem?: number;
  required?: boolean;
  errorMessage?: string;
  isAddNew?: boolean;
  itemNameDefault?: string;
  refreshKey?: number | string;
  onChange?: (newValue: T | T[] | null) => void;
  onAddNewOption?: (newValue: T) => void;
  callApiFn: (payload: IPayloadCallApiFn) => Promise<IResultCallApiFn<T>>;
};

const cx = classNames.bind(styles);

const BaseAutoCompleteComponent = <T extends IBaseOption>(props: Props<T>) => {
  //#region Destructuring Props
  const {
    id,
    label,
    required,
    multiple = true,
    maxItem,
    width = '100%',
    height = 'auto',
    errorMessage,
    value,
    isAddNew = false,
    itemNameDefault,
    refreshKey,
    placeholder,
    onChange,
    onAddNewOption,
    callApiFn,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const filter = createFilterOptions<T>();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [autocompleteValue, setAutocompleteValue] = useState<T | T[] | null>(multiple ? [] : null);
  const [inputValue, setInputValue] = useState<string>(EMPTY_STRING);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_CURRENT_PAGE);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [options, setOptions] = useState<ReadonlyArray<T>>([]);
  //#endregion Declare State

  //#region Declare Memo
  const convertValueToOptions = useCallback(
    (value?: string | string[] | null): T | T[] | null => {
      if (!value) return multiple ? [] : null;

      if (multiple && Array.isArray(value)) {
        return value.map((val) => {
          const existingOption = options.find((option) => String(option.value) === String(val));
          return (
            existingOption ||
            ({
              value: val,
              label: itemNameDefault || String(val),
            } as T)
          );
        });
      } else if (!multiple && typeof value === 'string') {
        const existingOption = options.find((option) => String(option.value) === String(value));
        return (
          existingOption ||
          ({
            value: value,
            label: itemNameDefault || String(value),
          } as T)
        );
      } else if (!multiple && Array.isArray(value) && value.length > 0) {
        const firstValue = value[0];
        const existingOption = options.find((option) => String(option.value) === String(firstValue));
        return (
          existingOption ||
          ({
            value: firstValue,
            label: itemNameDefault || String(firstValue),
          } as T)
        );
      } else if (multiple && typeof value === 'string') {
        const existingOption = options.find((option) => option.value === value);
        return [
          existingOption ||
            ({
              value: value,
              label: itemNameDefault || String(value),
            } as T),
        ];
      }

      return multiple ? [] : null;
    },
    [itemNameDefault, multiple, options]
  );
  //#endregion Declare Memo

  const optionsWithLoading = useMemo(() => {
    if (isLoading && hasMore) {
      return [
        ...options,
        {
          value: LOADING_OPTION_VALUE,
          label: '',
        } as T,
      ];
    }
    return options;
  }, [hasMore, isLoading, options]);

  //#region Implement Hook
  useEffect(() => {
    const convertedValue = convertValueToOptions(value);
    setAutocompleteValue(convertedValue);
  }, [value, convertValueToOptions]);

  useEffect(() => {
    setOptions([]);
    setPage(DEFAULT_CURRENT_PAGE);
    setHasMore(true);
    handleFetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callApiFn, refreshKey]);
  //#endregion Implement Hook

  //#region Declare Memo
  const computedPlaceholder = useMemo(() => {
    if (!multiple) return placeholder || t('common_text_search');

    if (Array.isArray(autocompleteValue) && autocompleteValue.length === 0)
      return placeholder || t('common_text_search');

    return EMPTY_STRING;
  }, [autocompleteValue]);
  //#endregion Declare Memo

  //#region Handle Function
  const handleFetchData = async (isFirstLoad: boolean = false) => {
    if (isLoading || (!isFirstLoad && !hasMore)) return;
    setIsLoading(true);

    try {
      const updatedPage: number = isFirstLoad ? DEFAULT_CURRENT_PAGE : page;
      const resp: IResultCallApiFn<T> = await callApiFn({ page: updatedPage });
      const { optionsResp, hasMoreResp } = resp;

      setOptions((prev) => (isFirstLoad ? optionsResp : [...prev, ...optionsResp]));
      setHasMore(hasMoreResp);
      setPage(updatedPage + 1);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleAutocompleteChange = (event: SyntheticEvent, newValue: T | T[] | null) => {
    if (!newValue) {
      setAutocompleteValue(multiple ? [] : null);
      onChange && onChange(multiple ? [] : null);
      return;
    }

    if (Array.isArray(newValue)) {
      newValue = newValue.filter((item) => item.value !== LOADING_OPTION_VALUE) as T[];
    } else if (newValue && newValue.value === LOADING_OPTION_VALUE) {
      return;
    }

    if (Array.isArray(newValue) && !!maxItem && newValue.length > maxItem) {
      return;
    }

    if (isAddNew) {
      const isNewOption = (option: T) => !options.some((o) => o.value === option.value);

      if (multiple && Array.isArray(newValue)) {
        const newItems = newValue.filter(isNewOption);
        if (newItems.length && onAddNewOption) {
          newItems.forEach((item) => onAddNewOption?.(item));
        }
      } else if (!multiple && newValue && isNewOption(newValue as T)) {
        onAddNewOption?.(newValue as T);
      }
    }
    setAutocompleteValue(newValue);
    onChange && onChange(newValue);
  };

  const handleInputChange = (_event: SyntheticEvent, value: string, _reason: AutocompleteInputChangeReason) => {
    setInputValue(value);
  };

  const handleAddItem = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === SPACE) {
      event.stopPropagation();
    }

    if (event.key === ENTER_KEY) {
      event.preventDefault();
    }
  };

  const handleScrollListbox = (event: UIEvent<HTMLUListElement>) => {
    const target = event.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;

    if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !isLoading) {
      handleFetchData();
    }
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='baseAutoCompleteComponentComponent' className={cx('container')}>
      {label && <InputLabel required={required}>{label}</InputLabel>}

      <Autocomplete
        {...(multiple && { disableCloseOnSelect: true, filterSelectedOptions: true })}
        id={id || 'autocomplete'}
        multiple={multiple}
        size='small'
        sx={{
          '.MuiInputBase-sizeSmall': {
            gap: '4px',
            padding: '10px 72px 10px 15px !important',
            '.MuiAutocomplete-input': {
              padding: '0px !important',
            },
          },

          '.MuiAutocomplete-endAdornment': {
            right: '16px !important',
          },
        }}
        clearIcon={
          <IconButton color='primary' size='small'>
            <img src={icons.commonIconClose} alt={t('common_alt_img')} className={cx('icon')} />
          </IconButton>
        }
        className={cx('autocomplete')}
        options={optionsWithLoading || []}
        getOptionLabel={(option: T) => (option.value === LOADING_OPTION_VALUE ? EMPTY_STRING : String(option.label))}
        value={autocompleteValue}
        inputValue={inputValue}
        onChange={handleAutocompleteChange}
        onKeyDown={(event) => handleAddItem(event)}
        onInputChange={handleInputChange}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        slotProps={{
          paper: {
            className: cx(
              'paper',
              Array.isArray(autocompleteValue) && !!maxItem && autocompleteValue.length >= maxItem && 'optionDisable'
            ),
          },
          listbox: {
            className: cx('listBox'),
            onScroll: handleScrollListbox,
            sx: {
              '.MuiAutocomplete-option': {
                borderRadius: '8px',
                fontSize: '14px',
                color: varColor.textSelectOption,
              },
              '.MuiAutocomplete-option.Mui-focused': {
                backgroundColor: 'transparent',
              },
              '.MuiAutocomplete-option[aria-selected="false"]:hover': {
                backgroundColor: varColor.bgHover,
              },
              '.MuiAutocomplete-option[aria-selected="true"]': {
                backgroundColor: varColor.secondary,
              },
              '.MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
                backgroundColor: varColor.secondary,
              },
            },
          },
          chip: {
            className: cx('chip'),
          },
        }}
        filterOptions={(options, params) => {
          const optionsWithoutLoading = options.filter((option) => option.value !== LOADING_OPTION_VALUE);

          const filtered = filter(optionsWithoutLoading, params);
          if (Array.isArray(autocompleteValue) && !!maxItem && autocompleteValue.length >= maxItem) {
            return filtered;
          }

          if (isAddNew) {
            const { inputValue } = params;

            const isExisting = options.some((option) => inputValue.toLowerCase() === option.label.toLowerCase());
            if (inputValue !== EMPTY_STRING && !isExisting) {
              filtered.push({
                label: inputValue,
                value: inputValue,
              } as T);
            }
          }

          return filtered;
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          const isNewOption = !options.some((o) => o.value === option.value);
          return (
            <Box
              component='li'
              key={option.value}
              {...rest}
              sx={{
                '&.Mui-focused': {
                  backgroundColor: `${alpha(varColor.primary, 0.1)} !important`,
                  color: `${varColor.textPrimary} !important`,
                },
              }}
            >
              {isAddNew && isNewOption && (
                <Box
                  sx={{
                    marginRight: '8px',
                  }}
                >
                  <CommonIconPlus strokePath={varColor.primary} />
                </Box>
              )}
              {option.label}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            focused={false}
            className={cx('textField')}
            sx={{
              '.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                width,
                height,
                backgroundColor: 'white',
              },
              '.MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                color: varColor.textPrimary,
              },
            }}
            placeholder={computedPlaceholder}
            disabled={Array.isArray(autocompleteValue) && !!maxItem && autocompleteValue.length >= maxItem}
          />
        )}
        popupIcon={<img src={icons.commonIconArrowBottom} alt={t('common_alt_img')} className={cx('arrowIcon')} />}
        noOptionsText={<Typography className={cx('noOptions')}>{t('common_no_data_available')}</Typography>}
      />

      {errorMessage && <span className={cx('errMessage')}>{errorMessage}</span>}
    </Box>
  );
};

export default BaseAutoCompleteComponent;
